/**
 * @fileoverview Serialize Better Auth / fetch onError values for logging.
 *
 * @purpose Pure extraction of safe, serializable fields from unknown errors
 *          (Response, Error, plain objects). Used by better-auth client onError.
 * @usage import { extractErrorInfo } from '@korvo/core/lib/better-auth/extract-error-info'
 * @ownership Core infrastructure — authentication client helpers
 * @dependencies None (browser-safe; no Node-only APIs required for core path)
 * @restrictions DO NOT log secrets; DO NOT throw — always return a plain object.
 *
 * @see packages/core/src/lib/better-auth/client.ts
 */

const TOP_LEVEL_KEYS = ['message', 'body', 'data', 'status', 'statusCode', 'code'] as const;
const RESERVED_KEYS = new Set(['response', 'responseText', 'request', 'error']);

function responseFields(error: unknown): Record<string, unknown> | null {
  if (typeof Response === 'undefined' || !(error instanceof Response)) {
    return null;
  }
  return {
    status: error.status,
    statusText: error.statusText,
    url: error.url,
    hint: 'Check server logs for the real error (e.g. BETTER_AUTH_SECRET, DB).',
  };
}

function errorInstanceFields(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof Error)) {
    return null;
  }
  return {
    message: error.message,
    stack: error.stack,
  };
}

function responseObjectFields(response: Response | Record<string, unknown>): Record<string, unknown> {
  return {
    status: (response as Response).status ?? (response as Record<string, unknown>).status,
    statusText: (response as Response).statusText ?? (response as Record<string, unknown>).statusText,
    url: (response as Response).url ?? (response as Record<string, unknown>).url,
  };
}

function requestObjectFields(request: Request | Record<string, unknown>): Record<string, unknown> {
  return {
    method: (request as Request).method ?? (request as Record<string, unknown>).method,
    url: (request as Request).url ?? (request as Record<string, unknown>).url,
  };
}

function nestedErrorValue(nestedError: unknown): unknown {
  if (nestedError && typeof nestedError === 'object' && 'message' in nestedError && 'name' in nestedError) {
    return {
      message: (nestedError as { message?: unknown }).message,
      name: (nestedError as { name?: unknown }).name,
    };
  }
  if (typeof nestedError === 'object' && nestedError !== null) {
    return nestedError;
  }
  return String(nestedError);
}

function isSerializableValue(value: unknown): boolean {
  if (value === null) {
    return true;
  }
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return true;
  }
  if (t === 'object' && !(value instanceof Response) && !(value instanceof Request)) {
    return true;
  }
  return false;
}

function mergeTopLevelObjectKeys(errorObj: Record<string, unknown>, errorInfo: Record<string, unknown>): void {
  for (const key of TOP_LEVEL_KEYS) {
    if (!(key in errorObj) || errorObj[key] === undefined) {
      continue;
    }
    const v = errorObj[key];
    if (isSerializableValue(v)) {
      errorInfo[key] = v;
    }
  }
  if (errorObj.response) {
    const response = errorObj.response as Response | Record<string, unknown>;
    errorInfo.response = responseObjectFields(response);
  }
  if (errorObj.responseText !== undefined && errorObj.responseText !== null) {
    try {
      const responseText = String(errorObj.responseText);
      errorInfo.responseText =
        responseText.length > 500 ? `${responseText.substring(0, 500)}...` : responseText;
    } catch {
      errorInfo.responseText = '[Unable to serialize]';
    }
  }
  if (errorObj.request) {
    const request = errorObj.request as Request | Record<string, unknown>;
    errorInfo.request = requestObjectFields(request);
  }
  if (errorObj.error !== undefined && errorObj.error !== null) {
    errorInfo.error = nestedErrorValue(errorObj.error);
  }
}

function mergeEnumerableKeys(errorObj: Record<string, unknown>, errorInfo: Record<string, unknown>): void {
  try {
    for (const key of Object.keys(errorObj)) {
      if (RESERVED_KEYS.has(key)) {
        continue;
      }
      try {
        const value = errorObj[key];
        if (isSerializableValue(value)) {
          errorInfo[key] = value;
        }
      } catch {
        // skip property
      }
    }
  } catch {
    // enumeration failed
  }
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[non-serializable]';
  }
}

function appendRawAndBody(error: unknown, errorInfo: Record<string, unknown>): void {
  errorInfo.raw = error === null || error === undefined ? String(error) : String(error);
  errorInfo._body = safeJsonStringify(error);
}

function appendSparseFallback(error: unknown, errorInfo: Record<string, unknown>): void {
  const keyCount = Object.keys(errorInfo).length;
  if (keyCount <= 1) {
    errorInfo.raw = error === null || error === undefined ? String(error) : String(error);
    errorInfo.jsonString = safeJsonStringify(error);
    if (error === null || error === undefined) {
      errorInfo.nullOrUndefined = true;
    }
  }
  appendRawAndBody(error, errorInfo);
  if (Object.keys(errorInfo).length <= 2) {
    errorInfo.hint = 'Check server logs (e.g. BETTER_AUTH_SECRET, DB) or network tab for status/body.';
  }
}

/**
 * Build a plain object safe to pass to console.error (never empty {} without raw/_body).
 */
export function extractErrorInfo(error: unknown): Record<string, unknown> {
  const errorInfo: Record<string, unknown> = {
    type:
      error !== null && error !== undefined && typeof error === 'object'
        ? error.constructor?.name ?? typeof error
        : typeof error,
  };

  const fromResponse = responseFields(error);
  if (fromResponse) {
    Object.assign(errorInfo, fromResponse);
  }

  const fromError = errorInstanceFields(error);
  if (fromError) {
    Object.assign(errorInfo, fromError);
  }

  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    mergeTopLevelObjectKeys(errorObj, errorInfo);
    mergeEnumerableKeys(errorObj, errorInfo);
  }

  appendSparseFallback(error, errorInfo);
  return errorInfo;
}
